import React, { useEffect } from 'react';
import AdvisoryFlowProgressBar from './toolbar/ProgressBar';
import AdvisoryAppShell from './app-shell/AdvisoryAppShell';
import AdvisoryFlowLayout from './app-shell/AdvisoryFlowLayout';
import FlowsFormProvider from '../../../../store/context/FlowsFormProvider';
import useCurrentGroup from '../../../../hooks/useCurrentGroup';
import InputSelector from '../../../input/InputSelector';
import HomeQuestionMap from './HomeQuestionMap';
import useDisplayHomeWrapper from '../hooks/useDisplayHomeWrapper';
import { useParams } from 'react-router';
import {
  useFlowsDispatch,
  useFlowsSelector,
} from '../../../../../../common/src/utils/globalVariables';
import { addVisitedTag } from '../../../../store/redux/slices/flowsSlice';
import ActiveSessionModal from './ActiveSessionModal';
import { setIsSessionActive } from '../../../../store/redux/slices/flowsMetaDataSlice';
import { useDisclosure } from '@chakra-ui/hooks';
import useInputGridConfig from '../../common/hooks/useInputGridConfig';
import { useFlowsRouteParams } from '../../../../hooks/useFlowsRouteParams';
import { useQueryClient } from 'react-query';
import useAuth from '../../../../../../app/src/features/authentication/hooks/useAuth';
import { useLouiseDispatch } from '../../../../../../sales-conversation/src/store/redux/store';
import { resetOffers } from '../../../../../../sales-conversation/src/store/redux/slices/offersSlice';
import ProgressBarMobile from './toolbar/ProgressBarMobile';

const AdvisoryFlowView: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { isAuthorized, isAccessTokenExpired } = useAuth();
  const { position } = useParams<{ position: string }>();

  const flowsDispatch = useFlowsDispatch();
  const dispatch = useLouiseDispatch();
  const isSessionActive = useFlowsSelector(
    (state) => state.flowsMetaData.isSessionActive,
  );
  const { isFullScreen } = useInputGridConfig();
  const currentGroup = useCurrentGroup();
  const isHomeQuestion = useDisplayHomeWrapper();

  const {
    isOpen: isOpenActiveSessionModal,
    onClose: onCloseActiveSessionModal,
    onOpen: onOpenActiveSessionModal,
  } = useDisclosure();

  useEffect(() => {
    if (isSessionActive && isAuthorized && !isAccessTokenExpired)
      onOpenActiveSessionModal();
    else flowsDispatch(setIsSessionActive(true));
  }, []);

  useEffect(() => {
    if (currentGroup) flowsDispatch(addVisitedTag(currentGroup?.tag));
  }, [position]);

  const { sessionId } = useFlowsRouteParams();

  // TEMPORARY
  const queryClient = useQueryClient();
  useEffect(() => {
    // Removes all queries related to this session
    // If query cache is ever persisted this should be moved to the function call
    // when a new session is created
    queryClient.removeQueries([sessionId, 'quotes']);
    queryClient.removeQueries([sessionId, 'products']);
    dispatch(resetOffers());
  }, []);

  return (
    <AdvisoryAppShell
      toolbarContent={<AdvisoryFlowProgressBar />}
      mobileToolbarContent={<ProgressBarMobile />}
    >
      <ActiveSessionModal
        isOpen={isOpenActiveSessionModal}
        onClose={onCloseActiveSessionModal}
      />
      {isHomeQuestion && <HomeQuestionMap />}
      <FlowsFormProvider
        key={`group_${currentGroup.id}${
          currentGroup.index != null ? `_${currentGroup.index}` : ''
        }`}
      >
        {isFullScreen ? (
          <InputSelector
            key={'group_' + currentGroup.id}
            question={currentGroup.questions[0]}
          />
        ) : (
          <AdvisoryFlowLayout />
        )}
      </FlowsFormProvider>
    </AdvisoryAppShell>
  );
};

export default AdvisoryFlowView;
